import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Factory,
  TrendingUp,
  Users,
  Activity,
  RefreshCw,
  Calendar,
  Building2,
  BarChart3,
  Zap,
  Package
} from 'lucide-react';
import { 
  useDailyProductionData, 
  useProductionStats,
  useProductionChartData,
  usePackagingStats 
} from '@/hooks/useProductionData';
import { 
  getCushionData, 
  transformCushionToDaily, 
  calculateCushionStats, 
  getCushionChartData 
} from '@/integrations/sqlserver/cushionService';
import { CushionFilters, DailyCushionData, CushionStats } from '@/types/cushion';
import { useSupabaseCompanies } from '@/hooks/useSupabaseCompanies';
import { ProductionFilters } from '@/types/production';
import { ProductionCharts } from '@/components/ProductionCharts';
import { PackagingCharts } from '@/components/PackagingCharts';
import { useQueryClient } from '@tanstack/react-query';
import { PackagingTable } from '@/components/PackagingTable';
import { RecentDaysTable } from '@/components/RecentDaysTable';
import { RecentPackagingTable } from '@/components/RecentPackagingTable';
import { CushionCharts } from '@/components/CushionCharts';
import { CushionTable } from '@/components/CushionTable';
import { CushionRecentDaysTable } from '@/components/CushionRecentDaysTable';
import { 
  getMetalData, 
  transformMetalToDaily, 
  calculateMetalStats, 
  getMetalChartData 
} from '@/integrations/sqlserver/metalService';
import { MetalFilters, DailyMetalData, MetalStats } from '@/types/metal';
import MetalCharts from '@/components/MetalCharts';
import MetalTable from '@/components/MetalTable';
import MetalRecentDaysTable from '@/components/MetalRecentDaysTable';
import MetalFinishedCharts from '@/components/MetalFinishedCharts';
import MetalFinishedTable from '@/components/MetalFinishedTable';
import MetalFinishedRecentTable from '@/components/MetalFinishedRecentTable';
import MetalAverageTable from '@/components/MetalAverageTable';
import CushionAverageTable from '@/components/CushionAverageTable';

export default function Production() {
  const queryClient = useQueryClient();
  
  // Current date for default filters
  const today = new Date().toISOString().split('T')[0];
  const lastWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [filters, setFilters] = useState<ProductionFilters>({
    fromDate: lastWeek,
    toDate: today,
    compID: undefined,
  });

  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [isRefetching, setIsRefetching] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Cushion data state
  const [cushionData, setCushionData] = useState<DailyCushionData[]>([]);
  const [cushionStats, setCushionStats] = useState<CushionStats | null>(null);
  const [cushionLoading, setCushionLoading] = useState(false);
  const [cushionError, setCushionError] = useState<string | null>(null);
  
  // Metal data state
  const [metalData, setMetalData] = useState<DailyMetalData[]>([]);
  const [metalStats, setMetalStats] = useState<MetalStats | null>(null);
  const [metalLoading, setMetalLoading] = useState(false);
  const [metalError, setMetalError] = useState<string | null>(null);
  
  // Tab states for different factory types
  const [activeCushionTab, setActiveCushionTab] = useState<string>("general");
  const [activeWoodTab, setActiveWoodTab] = useState<string>("general");
  const [activeMetalTab, setActiveMetalTab] = useState<string>("general");

  // Fetch companies for filter
  const { data: allCompanies = [], isLoading: companiesLoading } = useSupabaseCompanies({
    is_active: true
  });

  // Filter and sort companies (remove legacy ID 23, 24 and sort by name)
  const companies = allCompanies
    .filter(company => 
      parseInt(company.legacy_id) !== 23 && 
      parseInt(company.legacy_id) !== 24
    )
    .sort((a, b) => a.company_name.localeCompare(b.company_name));

  // Fetch production data
  const {
    data: dailyData = [],
    isLoading: dataLoading,
    error: dataError,
    refetch: refetchDailyData
  } = useDailyProductionData(filters);

  const {
    data: stats,
    isLoading: statsLoading,
    refetch: refetchStats
  } = useProductionStats(filters);

  const {
    data: packagingStats,
    isLoading: packagingStatsLoading,
    refetch: refetchPackagingStats
  } = usePackagingStats(filters);

  // Auto-select first company on load
  useEffect(() => {
    if (!hasInitialized && !companiesLoading && companies.length > 0 && !selectedCompany) {
      const firstCompany = companies[0];
      const firstCompanyId = parseInt(firstCompany.legacy_id);
      
      console.log('🎯 Auto-selecting first company:', firstCompany.company_name, 'ID:', firstCompanyId);
      
      setSelectedCompany(firstCompanyId);
      updateFilters({ compID: firstCompanyId });
      setHasInitialized(true);
    }
  }, [companies, companiesLoading, selectedCompany, hasInitialized]);

  // Sort daily data by date (newest first)
  const sortedDailyData = useMemo(() => {
    return [...dailyData].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [dailyData]);

  // Debug: Log daily data for table
  useEffect(() => {
    if (dailyData.length > 0) {
      console.log('🔢 Daily production data for table:', dailyData.length, 'records');
      console.log('🔢 Sample daily data:', dailyData[0]);
      console.log('🔢 Sample processes:', dailyData[0]?.processes);
    }
  }, [dailyData]);

  // Update filters
  const updateFilters = (newFilters: Partial<ProductionFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  // Check if current company is cushion/foam factory based on legacy_id
  const isCushionFactory = useMemo(() => {
    if (!selectedCompany) return false;
    
    // Cushion factory legacy_ids (from Supabase companies table)
    // Add more legacy_ids here if there are other cushion factories
    const cushionFactoryIds = [1]; // "Xưởng nệm" has legacy_id = 1
    
    return cushionFactoryIds.includes(selectedCompany);
  }, [selectedCompany]);

  // Check if current company is metal factory based on legacy_id
  const isMetalFactory = useMemo(() => {
    if (!selectedCompany) return false;
    
    // Metal factory legacy_ids (from Supabase companies table)
    const metalFactoryIds = [20]; // "Xưởng sắt" has legacy_id = 20
    
    return metalFactoryIds.includes(selectedCompany);
  }, [selectedCompany]);

  // Auto-select first tab when cushion data is loaded (only once)
  useEffect(() => {
    if (isCushionFactory && cushionData.length > 0 && !cushionLoading && activeCushionTab === "general") {
      console.log('🎯 Cushion data loaded, first tab already active');
      // Only log, don't change tab to prevent interference with user selection
    }
  }, [isCushionFactory, cushionData, cushionLoading]);

  // Auto-select first tab when wood production data is loaded (only once)
  useEffect(() => {
    if (!isCushionFactory && !isMetalFactory && dailyData.length > 0 && !dataLoading && activeWoodTab === "general") {
      console.log('🎯 Wood production data loaded, first tab already active');
      // Only log, don't change tab to prevent interference with user selection
    }
  }, [isCushionFactory, isMetalFactory, dailyData, dataLoading]);

  // Auto-select first tab when metal production data is loaded (only once)
  useEffect(() => {
    if (isMetalFactory && metalData.length > 0 && !metalLoading && activeMetalTab === "general") {
      console.log('🎯 Metal production data loaded, first tab already active');
      // Only log, don't change tab to prevent interference with user selection
    }
  }, [isMetalFactory, metalData, metalLoading]);

  // Fetch cushion data when needed
  const fetchCushionData = async (cushionFilters: CushionFilters) => {
    setCushionLoading(true);
    setCushionError(null);
    
    try {
      console.log('🏭 Fetching cushion data for filters:', cushionFilters);
      
      const rawData = await getCushionData(cushionFilters);
      const dailyData = transformCushionToDaily(rawData);
      const stats = calculateCushionStats(dailyData);
      
      setCushionData(dailyData);
      setCushionStats(stats);
      
      console.log('✅ Cushion data loaded:', dailyData.length, 'records');
    } catch (error: any) {
      console.error('❌ Failed to fetch cushion data:', error);
      setCushionError(error.message || 'Failed to load cushion data');
    } finally {
      setCushionLoading(false);
    }
  };

  // Fetch metal data when needed
  const fetchMetalData = async (metalFilters: MetalFilters) => {
    setMetalLoading(true);
    setMetalError(null);
    
    try {
      console.log('🔥 Fetching metal data for filters:', metalFilters);
      const rawData = await getMetalData(metalFilters);
      const transformedData = transformMetalToDaily(rawData);
      const stats = calculateMetalStats(transformedData);
      
      setMetalData(transformedData);
      setMetalStats(stats);
      console.log('🔥 Metal data fetched successfully:', transformedData.length, 'days');
    } catch (error: any) {
      console.error('🔥 Error fetching metal data:', error);
      
      // Provide more user-friendly error messages
      let errorMessage = 'Không thể tải dữ liệu sản xuất sắt';
      
      if (error.message?.includes('Date range too large')) {
        errorMessage = 'Khoảng thời gian quá dài (tối đa 365 ngày). Vui lòng chọn khoảng thời gian ngắn hơn.';
      } else if (error.message?.includes('500')) {
        errorMessage = 'Server đang bận. Vui lòng thử lại với khoảng thời gian ngắn hơn.';
      } else if (error.message?.includes('timeout') || error.message?.includes('timeout')) {
        errorMessage = 'Tải dữ liệu quá lâu. Vui lòng thử lại với khoảng thời gian ngắn hơn.';
      } else if (error.message?.includes('All metal data fetching strategies failed')) {
        errorMessage = 'Không thể kết nối đến server. Vui lòng kiểm tra kết nối mạng và thử lại.';
      }
      
      setMetalError(errorMessage);
    } finally {
      setMetalLoading(false);
    }
  };

  // Auto-fetch cushion data when it's a cushion factory
  useEffect(() => {
    if (isCushionFactory && filters.compID && filters.fromDate && filters.toDate) {
      const cushionFilters: CushionFilters = {
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        compID: filters.compID
      };
      fetchCushionData(cushionFilters);
    }
  }, [isCushionFactory, filters]);

  // Auto-fetch metal data when it's a metal factory
  useEffect(() => {
    if (isMetalFactory && filters.compID && filters.fromDate && filters.toDate) {
      const metalFilters: MetalFilters = {
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        compID: filters.compID
      };
      fetchMetalData(metalFilters);
    }
  }, [isMetalFactory, filters]);

  // Refetch all data
  const refetchAllData = () => {
    setIsRefetching(true);
    console.log('🔄 Starting data refresh for filters:', filters);
    
    const promises = [
      refetchDailyData(),
      refetchStats(),
      refetchPackagingStats()
    ];

    // Also refetch cushion data if it's a cushion factory
    if (isCushionFactory && filters.compID) {
      const cushionFilters: CushionFilters = {
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        compID: filters.compID
      };
      promises.push(fetchCushionData(cushionFilters) as any);
    }

    // Also refetch metal data if it's a metal factory
    if (isMetalFactory && filters.compID) {
      const metalFilters: MetalFilters = {
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        compID: filters.compID
      };
      promises.push(fetchMetalData(metalFilters) as any);
    }
    
    Promise.all(promises).then(() => {
      console.log('✅ All production data refreshed successfully');
      console.log('📊 Updated filters applied:', filters);
    }).catch((error) => {
      console.error('❌ Error refreshing production data:', error);
    }).finally(() => {
      setIsRefetching(false);
      console.log('🏁 Data refresh completed');
    });
  };

  const handleCompanySelect = (compId: number) => {
    // Always select the clicked company (no deselection allowed)
    const newSelectedCompany = compId;
    
    // Only refetch if it's a different company than currently selected
    const shouldRefetch = selectedCompany !== compId;
    
    setSelectedCompany(newSelectedCompany);
    
    // Update filters
    updateFilters({ compID: newSelectedCompany });
    
    // Find company name for logging
    const selectedCompanyName = companies.find(c => parseInt(c.legacy_id) === compId)?.company_name || 'Unknown';
    
    if (shouldRefetch) {
      // Trigger all data refetch only when switching to a different company
      console.log('🏭 Company switched to:', selectedCompanyName, '- Starting data refresh...');
      refetchAllData();
    } else {
      console.log('🏭 Same company clicked:', selectedCompanyName, '- No refresh needed');
    }
  };

  const handleRefresh = () => {
    refetchAllData();
  };

  const handleClearCache = () => {
    // Clear all cache and reload page
    console.log('🗑️ Clearing all cache and reloading...');
    queryClient.clear();
    window.location.reload();
  };

  // Format number with decimal places
  const formatNumber = (value: number | null, decimals: number = 2) => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(decimals);
  };

  // Calculate min/max for M³ values in each process for detailed table
  const detailedM3Stats = React.useMemo(() => {
    if (sortedDailyData.length === 0) return {};
    
    const processes = ['soThe', 'tinhChe', 'lapRap', 'nguoi', 'son'] as const;
    const stats: Record<string, { min: number; max: number }> = {};
    
    processes.forEach(process => {
      const values = sortedDailyData
        .map(item => item.processes?.[process]?.volume)
        .filter((value): value is number => value !== null && value !== undefined && value > 0);
      
      if (values.length > 1) { // Only highlight if we have multiple values to compare
        stats[process] = {
          min: Math.min(...values),
          max: Math.max(...values)
        };
      }
    });
    
    return stats;
  }, [sortedDailyData]);

  // Get conditional styling for M³ values in detailed table
  const getDetailedM3Styling = (value: number | null, processName: string) => {
    if (!value || value <= 0 || !detailedM3Stats[processName]) {
      return 'text-sm border-r text-center bg-amber-50 font-semibold';
    }
    
    const { min, max } = detailedM3Stats[processName];
    if (value === max) {
      return 'text-sm border-r text-center bg-green-600 text-white font-semibold'; // Highest value: green background, white text
    } else if (value === min) {
      return 'text-sm border-r text-center bg-red-600 text-white font-semibold'; // Lowest value: red background, white text
    }
    
    return 'text-sm border-r text-center bg-amber-50 font-semibold'; // Default amber styling
  };

  // Calculate M3 averages for each process
  const m3Averages = React.useMemo(() => {
    if (sortedDailyData.length === 0) return {};
    
    const processes = [
      { key: 'soThe', name: 'Sơ chế' },
      { key: 'tinhChe', name: 'Tinh chế' },
      { key: 'lapRap', name: 'Lắp ráp' },
      { key: 'nguoi', name: 'Nguội' },
      { key: 'son', name: 'Sơn' }
    ] as const;
    
    const averages: Record<string, { name: string; totalDays: number; totalWork: number; totalM3: number; avgPerDay: number; avgPerWork: number }> = {};
    
    processes.forEach(process => {
      const validData = sortedDailyData.filter(item => {
        const processData = item.processes?.[process.key];
        return processData && (processData.labor || 0) > 0 && (processData.volume || 0) > 0;
      });
      
      const totalDays = validData.length;
      const totalWork = validData.reduce((sum, item) => sum + (item.processes?.[process.key]?.labor || 0), 0);
      const totalM3 = validData.reduce((sum, item) => sum + (item.processes?.[process.key]?.volume || 0), 0);
      
      averages[process.key] = {
        name: process.name,
        totalDays,
        totalWork,
        totalM3,
        avgPerDay: totalDays > 0 ? totalM3 / totalDays : 0,
        avgPerWork: totalWork > 0 ? totalM3 / totalWork : 0
      };
    });
    
    return averages;
  }, [sortedDailyData]);

  if (dataError) {
    return (
      <div className="px-3 sm:px-6 sm:max-w-7xl mx-auto py-6">
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            <span>Lỗi khi tải dữ liệu sản xuất: {dataError.message}</span>
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 sm:max-w-7xl mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Factory className="h-8 w-8 text-blue-600" />
            Sản xuất
          </h1>
          <p className="text-muted-foreground">Theo dõi công lao động, công suất và năng suất sản xuất</p>
        </div>
        <Button onClick={handleRefresh} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Làm mới
        </Button>
      </div>

      {/* Filters - Compact Design */}
      <Card className="border-2 border-blue-100 bg-gradient-to-r from-blue-50/50 to-indigo-50/50">
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            {/* Date Range - Compact */}
            <div className="flex items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="fromDate" className="text-sm font-medium text-black">
                  📅 Từ ngày
                </Label>
              <Input
                id="fromDate"
                type="date"
                value={filters.fromDate}
                onChange={(e) => updateFilters({ fromDate: e.target.value })}
                  className="w-40"
              />
            </div>
            
              <div className="space-y-1">
                <Label htmlFor="toDate" className="text-sm font-medium text-black">
                  📅 Đến ngày
                </Label>
              <Input
                id="toDate"
                type="date"
                value={filters.toDate}
                onChange={(e) => updateFilters({ toDate: e.target.value })}
                  className="w-40"
              />
              </div>
            </div>

            {/* Company Filter - Compact */}
            <div className="flex-1 space-y-1">
              <Label className="text-sm font-medium text-black flex items-center gap-2">
                🏭 Xưởng sản xuất
                {isRefetching && (
                  <span className="text-xs text-amber-600 font-medium animate-pulse">
                    🔄 Đang tải dữ liệu...
                  </span>
                )}
                {selectedCompany && !isRefetching && (
                  <span className="text-xs text-green-600 font-medium">
                    ✅ Đã chọn: {companies.find(c => parseInt(c.legacy_id) === selectedCompany)?.company_name}
                  </span>
                )}
              </Label>
              {companiesLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <div className="flex flex-wrap gap-1.5 p-2 border rounded-md min-h-9 bg-white/70">
                  {companies.map((company) => (
                    <Badge
                      key={company.id}
                      variant={selectedCompany === parseInt(company.legacy_id) ? "default" : "outline"}
                      className={`cursor-pointer hover:scale-105 transition-all text-xs px-2 py-1 ${
                        isRefetching ? 'opacity-50 pointer-events-none' : ''
                      }`}
                      onClick={() => handleCompanySelect(parseInt(company.legacy_id))}
                    >
                      <Building2 className="h-3 w-3 mr-1" />
                      {company.company_name}
                      {selectedCompany === parseInt(company.legacy_id) && !isRefetching && (
                        <span className="ml-1 text-xs">✓</span>
                      )}
                    </Badge>
                  ))}
                </div>
              )}
            </div>


          </div>
        </CardContent>
      </Card>

      {/* Conditional Layout: Cushion vs Metal vs Wood Production */}
      {isCushionFactory ? (
        /* Cushion Production Layout with 3 Tabs */
        <div className="bg-gradient-to-r from-blue-50 to-slate-50 p-4 rounded-xl border-2 border-blue-100 relative">
          {/* Global Loading Overlay for Cushion */}
          {(cushionLoading || isRefetching) && (
            <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="flex items-center gap-3 bg-white px-6 py-4 rounded-lg shadow-lg border">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                <span className="text-base font-medium text-gray-700">Đang tải dữ liệu sản xuất nệm...</span>
              </div>
            </div>
          )}
          
          <Tabs value={activeCushionTab} onValueChange={setActiveCushionTab} className="space-y-4">
            {/* Cushion Tabs */}
            <div className="flex justify-center">
              <TabsList className="inline-flex items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground h-12 w-auto shadow-md border">
                <TabsTrigger 
                  value="general" 
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-blue-50"
                >
                  <Factory className="h-4 w-4" />
                  <span>Sản xuất chung</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="foam" 
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-orange-50"
                >
                  <Package className="h-4 w-4" />
                  <span>Mút</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="cotton" 
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-gray-600 data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-gray-50"
                >
                  <Activity className="h-4 w-4" />
                  <span>Gòn</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab 1: Sản xuất chung (Cắt vải, May, Thành phẩm) */}
            <TabsContent value="general" className="space-y-4 bg-white rounded-lg p-4 shadow-sm border border-blue-100">

              {/* Recent Days Table for General Production */}
              <CushionRecentDaysTable 
                dailyData={cushionData}
                isLoading={cushionLoading}
                error={cushionError}
                processes={['catVai', 'may', 'thanhPham']}
                title="Ba ngày gần nhất - Sản xuất chung"
              />

              {/* Average Table - Cushion General Processes */}
              <CushionAverageTable
                dailyData={cushionData} 
                isLoading={cushionLoading} 
                error={cushionError}
                processes={['catVai', 'may', 'thanhPham']}
                title="Dữ liệu trung bình M³ theo công đoạn sản xuất chung"
              />

              {/* Charts for General Production */}
              <CushionCharts 
                dailyData={cushionData}
                isLoading={cushionLoading}
                error={cushionError}
                processes={['catVai', 'may', 'thanhPham']}
                title="Biểu đồ sản xuất chung"
              />

              {/* Detailed Table for General Production */}
              <CushionTable 
                dailyData={cushionData}
                isLoading={cushionLoading}
                error={cushionError}
                processes={['catVai', 'may', 'thanhPham']}
                title="Dữ liệu sản xuất chung chi tiết"
              />
            </TabsContent>

            {/* Tab 2: Mút (Đổ mút, Cắt mút) */}
            <TabsContent value="foam" className="space-y-4 bg-white rounded-lg p-4 shadow-sm border border-orange-100">

              {/* Recent Days Table for Foam Production */}
              <CushionRecentDaysTable 
                dailyData={cushionData}
                isLoading={cushionLoading}
                error={cushionError}
                processes={['doMut', 'catMut']}
                title="Ba ngày gần nhất - Sản xuất mút"
              />

              {/* Average Table - Cushion Foam Processes */}
              <CushionAverageTable
                dailyData={cushionData} 
                isLoading={cushionLoading} 
                error={cushionError}
                processes={['doMut', 'catMut']}
                title="Dữ liệu trung bình M³ theo công đoạn sản xuất mút"
              />

              {/* Charts for Foam Production */}
              <CushionCharts 
                dailyData={cushionData}
                isLoading={cushionLoading}
                error={cushionError}
                processes={['doMut', 'catMut']}
                title="Biểu đồ sản xuất mút"
              />

              {/* Detailed Table for Foam Production */}
              <CushionTable 
                dailyData={cushionData}
                isLoading={cushionLoading}
                error={cushionError}
                processes={['doMut', 'catMut']}
                title="Dữ liệu sản xuất mút chi tiết"
              />
            </TabsContent>

            {/* Tab 3: Gòn */}
            <TabsContent value="cotton" className="space-y-4 bg-white rounded-lg p-4 shadow-sm border border-gray-100">

              {/* Recent Days Table for Cotton Production */}
              <CushionRecentDaysTable 
                dailyData={cushionData}
                isLoading={cushionLoading}
                error={cushionError}
                processes={['gon']}
                title="Ba ngày gần nhất - Sản xuất gòn"
              />

              {/* Average Table - Cushion Cotton Process */}
              <CushionAverageTable
                dailyData={cushionData} 
                isLoading={cushionLoading} 
                error={cushionError}
                processes={['gon']}
                title="Dữ liệu trung bình Kg theo công đoạn sản xuất gòn"
              />

              {/* Charts for Cotton Production */}
              <CushionCharts 
                dailyData={cushionData}
                isLoading={cushionLoading}
                error={cushionError}
                processes={['gon']}
                title="Biểu đồ sản xuất gòn"
              />

              {/* Detailed Table for Cotton Production */}
              <CushionTable 
                dailyData={cushionData}
                isLoading={cushionLoading}
                error={cushionError}
                processes={['gon']}
                title="Dữ liệu sản xuất gòn chi tiết"
              />
            </TabsContent>
          </Tabs>
        </div>
      ) : isMetalFactory ? (
        /* Metal Production Layout with Tabs */
        <div className="bg-gradient-to-r from-gray-50 to-amber-50 p-4 rounded-xl border-2 border-amber-100 relative">
          {/* Global Loading Overlay for Metal Production */}
          {(metalLoading || isRefetching) && (
            <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="flex items-center gap-3 bg-white px-6 py-4 rounded-lg shadow-lg border">
                <RefreshCw className="h-6 w-6 animate-spin text-amber-600" />
                                      <span className="text-base font-medium text-gray-700">
                        {(() => {
                          if (!filters.fromDate || !filters.toDate) return 'Đang tải dữ liệu sản xuất sắt...';
                          const daysDiff = Math.ceil((new Date(filters.toDate).getTime() - new Date(filters.fromDate).getTime()) / (1000 * 60 * 60 * 24));
                          if (daysDiff > 90) return `Đang tải dữ liệu sản xuất sắt (${daysDiff} ngày - có thể mất thời gian)...`;
                          return 'Đang tải dữ liệu sản xuất sắt...';
                        })()}
                      </span>
              </div>
            </div>
          )}
          
          <Tabs value={activeMetalTab} onValueChange={setActiveMetalTab} className="space-y-4">
            {/* Metal Tabs - 2 Tabs: Cơ khí and Thành phẩm */}
            <div className="flex justify-center">
              <TabsList className="inline-flex items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground h-12 w-auto shadow-md border">
                <TabsTrigger 
                  value="general" 
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-amber-600 data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-amber-50"
                >
                  <Factory className="h-4 w-4" />
                  <span>Cơ khí</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="finished" 
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-orange-600 data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-orange-50"
                >
                  <Package className="h-4 w-4" />
                  <span>Thành phẩm</span>
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Tab 1: Cơ khí (Phôi, Hàn, Sơn) */}
            <TabsContent value="general" className="space-y-4 bg-white rounded-lg p-4 shadow-sm border border-amber-100">
              
              {/* Recent Days Table */}
              <MetalRecentDaysTable
                dailyData={metalData} 
                isLoading={metalLoading} 
                error={metalError}
                processes={['phoi', 'han', 'son']}
                title="Ba ngày gần nhất - Cơ khí"
              />
              
              {/* Average Table - Metal Mechanical Processes */}
              <MetalAverageTable
                dailyData={metalData} 
                isLoading={metalLoading} 
                error={metalError}
                processes={['phoi', 'han', 'son']}
                title="Dữ liệu trung bình Kg theo công đoạn cơ khí"
              />
              
              {/* Metal Charts */}
              <MetalCharts
                dailyData={metalData} 
                isLoading={metalLoading} 
                error={metalError}
                processes={['phoi', 'han', 'son']}
                title="Biểu đồ sản xuất cơ khí"
              />
              
              {/* Metal Data Table */}
              <MetalTable
                dailyData={metalData} 
                isLoading={metalLoading} 
                error={metalError}
                processes={['phoi', 'han', 'son']}
                title="Dữ liệu sản xuất cơ khí chi tiết"
              />
            </TabsContent>

            {/* Tab 2: Thành phẩm (M3 Carton và Số Container) */}
            <TabsContent value="finished" className="space-y-4 bg-white rounded-lg p-4 shadow-sm border border-orange-100">
              
              {/* Recent Days Table - M3 Carton & Container */}
              <MetalFinishedRecentTable
                dailyData={metalData} 
                isLoading={metalLoading} 
                error={metalError}
                title="Ba ngày gần nhất - Thành phẩm"
              />
              
              {/* Finished Product Charts - M3 Carton & Container */}
              <MetalFinishedCharts
                dailyData={metalData} 
                isLoading={metalLoading} 
                error={metalError}
                title="Biểu đồ thành phẩm"
              />
              
              {/* Finished Product Data Table - M3 Carton & Container */}
              <MetalFinishedTable
                dailyData={metalData} 
                isLoading={metalLoading} 
                error={metalError}
                title="Dữ liệu thành phẩm chi tiết"
              />
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        /* Wood Production Layout (Original) */
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-xl border-2 border-gray-100 relative">
          {/* Global Loading Overlay for Wood Production */}
          {(dataLoading || statsLoading || packagingStatsLoading || isRefetching) && (
            <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="flex items-center gap-3 bg-white px-6 py-4 rounded-lg shadow-lg border">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
                <span className="text-base font-medium text-gray-700">Đang tải dữ liệu sản xuất gỗ...</span>
              </div>
            </div>
          )}
          
          <Tabs value={activeWoodTab} onValueChange={setActiveWoodTab} className="space-y-4">
          {/* Compact Toggle Buttons */}
          <div className="flex justify-center">
            <TabsList className="inline-flex items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground h-12 w-auto shadow-md border">
              <TabsTrigger 
                value="general" 
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-blue-50"
              >
                <Factory className="h-4 w-4" />
                <span>Sản xuất chung</span>
              </TabsTrigger>
              <TabsTrigger 
                value="packaging" 
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-sm hover:bg-green-50"
              >
                <Package className="h-4 w-4" />
                <span>Thành phẩm</span>
              </TabsTrigger>
            </TabsList>
          </div>

        {/* Tab 1: Sản xuất chung */}
        <TabsContent value="general" className="space-y-4 bg-white rounded-lg p-4 shadow-sm border border-blue-100">
          
          {/* Recent Days Table */}
          <RecentDaysTable filters={filters} />
          
          {/* M3 Average Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Dữ liệu trung bình M³ theo công đoạn
              </CardTitle>
              <CardDescription>
                Thống kê trung bình công suất M³ của từng công đoạn trong khoảng thời gian được chọn
              </CardDescription>
            </CardHeader>
            <CardContent className="p-3 sm:p-6">
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center font-medium border-r text-xs sm:text-sm px-2 sm:px-4 py-2 min-w-[80px]">Nội dung</TableHead>
                      <TableHead className="text-center font-medium border-r bg-blue-50 text-xs sm:text-sm px-1 sm:px-4 py-2 min-w-[60px]">Sơ chế</TableHead>
                      <TableHead className="text-center font-medium border-r bg-green-50 text-xs sm:text-sm px-1 sm:px-4 py-2 min-w-[60px]">Tinh chế</TableHead>
                      <TableHead className="text-center font-medium border-r bg-orange-50 text-xs sm:text-sm px-1 sm:px-4 py-2 min-w-[60px]">Lắp ráp</TableHead>
                      <TableHead className="text-center font-medium border-r bg-purple-50 text-xs sm:text-sm px-1 sm:px-4 py-2 min-w-[60px]">Nguội</TableHead>
                      <TableHead className="text-center font-medium bg-yellow-50 text-xs sm:text-sm px-1 sm:px-4 py-2 min-w-[60px]">Sơn</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="hover:bg-gray-50">
                      <TableCell className="font-medium border-r text-xs sm:text-sm px-2 sm:px-4 py-2">Tổng số ngày</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{m3Averages.soThe?.totalDays || 0}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{m3Averages.tinhChe?.totalDays || 0}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{m3Averages.lapRap?.totalDays || 0}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{m3Averages.nguoi?.totalDays || 0}</TableCell>
                      <TableCell className="text-center text-xs sm:text-sm px-1 sm:px-4 py-2">{m3Averages.son?.totalDays || 0}</TableCell>
                    </TableRow>
                    <TableRow className="hover:bg-gray-50">
                      <TableCell className="font-medium border-r text-xs sm:text-sm px-2 sm:px-4 py-2">Tổng số công</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.soThe?.totalWork || 0, 1)}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.tinhChe?.totalWork || 0, 1)}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.lapRap?.totalWork || 0, 1)}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.nguoi?.totalWork || 0, 1)}</TableCell>
                      <TableCell className="text-center text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.son?.totalWork || 0, 1)}</TableCell>
                    </TableRow>
                    <TableRow className="hover:bg-gray-50">
                      <TableCell className="font-medium border-r text-xs sm:text-sm px-2 sm:px-4 py-2">Tổng số m3</TableCell>
                      <TableCell className="text-center border-r bg-amber-50 font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.soThe?.totalM3 || 0, 2)}</TableCell>
                      <TableCell className="text-center border-r bg-amber-50 font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.tinhChe?.totalM3 || 0, 2)}</TableCell>
                      <TableCell className="text-center border-r bg-amber-50 font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.lapRap?.totalM3 || 0, 2)}</TableCell>
                      <TableCell className="text-center border-r bg-amber-50 font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.nguoi?.totalM3 || 0, 2)}</TableCell>
                      <TableCell className="text-center bg-amber-50 font-semibold text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.son?.totalM3 || 0, 2)}</TableCell>
                    </TableRow>
                    <TableRow className="hover:bg-gray-50">
                      <TableCell className="font-medium border-r text-xs sm:text-sm px-2 sm:px-4 py-2">TB/ngày</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.soThe?.avgPerDay || 0, 2)}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.tinhChe?.avgPerDay || 0, 2)}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.lapRap?.avgPerDay || 0, 2)}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.nguoi?.avgPerDay || 0, 2)}</TableCell>
                      <TableCell className="text-center text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.son?.avgPerDay || 0, 2)}</TableCell>
                    </TableRow>
                    <TableRow className="hover:bg-gray-50">
                      <TableCell className="font-medium border-r text-xs sm:text-sm px-2 sm:px-4 py-2">TB/công</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.soThe?.avgPerWork || 0, 3)}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.tinhChe?.avgPerWork || 0, 3)}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.lapRap?.avgPerWork || 0, 3)}</TableCell>
                      <TableCell className="text-center border-r text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.nguoi?.avgPerWork || 0, 3)}</TableCell>
                      <TableCell className="text-center text-xs sm:text-sm px-1 sm:px-4 py-2">{formatNumber(m3Averages.son?.avgPerWork || 0, 3)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

      {/* Production Charts */}
      <ProductionCharts filters={filters} />

      {/* Production Data Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Factory className="h-5 w-5" />
            Dữ liệu sản xuất chi tiết
          </CardTitle>
          <CardDescription>
            Công lao động, công suất và năng suất theo từng ngày và công đoạn
          </CardDescription>
        </CardHeader>
        <CardContent>
          {dataLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : sortedDailyData.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead rowSpan={2} className="vertical-align-bottom border-r w-[100px]">Ngày</TableHead>
                    
                    {/* Sơ chế */}
                    <TableHead colSpan={3} className="text-center border-r bg-blue-50">Sơ chế</TableHead>
                    
                    {/* Tinh chế */}
                    <TableHead colSpan={3} className="text-center border-r bg-green-50">Tinh chế</TableHead>
                    
                    {/* Lắp ráp */}
                    <TableHead colSpan={3} className="text-center border-r bg-orange-50">Lắp ráp</TableHead>
                    
                    {/* Nguội */}
                    <TableHead colSpan={3} className="text-center border-r bg-purple-50">Nguội</TableHead>
                    
                    {/* Sơn */}
                    <TableHead colSpan={3} className="text-center bg-yellow-50">Sơn</TableHead>
                  </TableRow>
                  <TableRow>
                    {/* Sơ chế */}
                    <TableHead className="text-xs border-r w-[50px]">Công</TableHead>
                    <TableHead className="text-xs border-r w-[45px] bg-amber-100 font-semibold">M³</TableHead>
                    <TableHead className="text-xs border-r w-[45px]">NS</TableHead>
                    
                    {/* Tinh chế */}
                    <TableHead className="text-xs border-r w-[50px]">Công</TableHead>
                    <TableHead className="text-xs border-r w-[45px] bg-amber-100 font-semibold">M³</TableHead>
                    <TableHead className="text-xs border-r w-[45px]">NS</TableHead>
                    
                    {/* Lắp ráp */}
                    <TableHead className="text-xs border-r w-[50px]">Công</TableHead>
                    <TableHead className="text-xs border-r w-[45px] bg-amber-100 font-semibold">M³</TableHead>
                    <TableHead className="text-xs border-r w-[45px]">NS</TableHead>
                    
                    {/* Nguội */}
                    <TableHead className="text-xs border-r w-[50px]">Công</TableHead>
                    <TableHead className="text-xs border-r w-[45px] bg-amber-100 font-semibold">M³</TableHead>
                    <TableHead className="text-xs border-r w-[45px]">NS</TableHead>
                    
                    {/* Sơn */}
                    <TableHead className="text-xs w-[50px]">Công</TableHead>
                    <TableHead className="text-xs w-[45px] bg-amber-100 font-semibold">M³</TableHead>
                    <TableHead className="text-xs w-[45px]">NS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedDailyData.map((item, index) => (
                    <TableRow key={index} className="hover:bg-gray-50">
                      <TableCell className="font-medium border-r">
                        {new Date(item.date).toLocaleDateString('vi-VN')}
                      </TableCell>
                      
                      {/* Sơ chế */}
                      <TableCell className="text-sm border-r text-center">{formatNumber(item.processes?.soThe?.labor, 1)}</TableCell>
                      <TableCell className={getDetailedM3Styling(item.processes?.soThe?.volume, 'soThe')}>{formatNumber(item.processes?.soThe?.volume, 2)}</TableCell>
                      <TableCell className="text-sm border-r text-center">{formatNumber(item.processes?.soThe?.productivity, 3)}</TableCell>
                      
                      {/* Tinh chế */}
                      <TableCell className="text-sm border-r text-center">{formatNumber(item.processes?.tinhChe?.labor, 1)}</TableCell>
                      <TableCell className={getDetailedM3Styling(item.processes?.tinhChe?.volume, 'tinhChe')}>{formatNumber(item.processes?.tinhChe?.volume, 2)}</TableCell>
                      <TableCell className="text-sm border-r text-center">{formatNumber(item.processes?.tinhChe?.productivity, 3)}</TableCell>
                      
                      {/* Lắp ráp */}
                      <TableCell className="text-sm border-r text-center">{formatNumber(item.processes?.lapRap?.labor, 1)}</TableCell>
                      <TableCell className={getDetailedM3Styling(item.processes?.lapRap?.volume, 'lapRap')}>{formatNumber(item.processes?.lapRap?.volume, 2)}</TableCell>
                      <TableCell className="text-sm border-r text-center">{formatNumber(item.processes?.lapRap?.productivity, 3)}</TableCell>
                      
                      {/* Nguội */}
                      <TableCell className="text-sm border-r text-center">{formatNumber(item.processes?.nguoi?.labor, 1)}</TableCell>
                      <TableCell className={getDetailedM3Styling(item.processes?.nguoi?.volume, 'nguoi')}>{formatNumber(item.processes?.nguoi?.volume, 2)}</TableCell>
                      <TableCell className="text-sm border-r text-center">{formatNumber(item.processes?.nguoi?.productivity, 3)}</TableCell>
                      
                      {/* Sơn */}
                      <TableCell className="text-sm text-center">{formatNumber(item.processes?.son?.labor, 1)}</TableCell>
                      <TableCell className={getDetailedM3Styling(item.processes?.son?.volume, 'son')}>{formatNumber(item.processes?.son?.volume, 2)}</TableCell>
                      <TableCell className="text-sm text-center">{formatNumber(item.processes?.son?.productivity, 3)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Factory className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Không có dữ liệu sản xuất trong khoảng thời gian được chọn</p>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Tab 2: Thành phẩm đóng gói */}
        <TabsContent value="packaging" className="space-y-4 bg-white rounded-lg p-4 shadow-sm border border-green-100">

          {/* Recent Packaging Days Table */}
          <RecentPackagingTable filters={filters} />

          {/* Packaging Summary Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                📊 Bảng tổng hợp chỉ tiêu thành phẩm
              </CardTitle>
              <CardDescription>
                Tổng hợp các chỉ tiêu chính theo m3 gỗ, m3 thùng và container
              </CardDescription>
            </CardHeader>
            <CardContent>
              {packagingStatsLoading ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : packagingStats ? (
                <div className="space-y-4">
                  {/* Summary Information Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-blue-700">📅 Tổng số ngày:</span>
                      <span className="font-bold text-lg text-blue-800">{packagingStats.totalDays}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-blue-700">👥 Tổng số công:</span>
                      <span className="font-bold text-lg text-blue-800">{formatNumber(packagingStats.totalLabor, 1)}</span>
                    </div>
                  </div>

                  {/* Simplified Table */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px] font-semibold border-r">Chỉ tiêu</TableHead>
                          <TableHead className="text-center font-semibold border-r">Tổng m3</TableHead>
                          <TableHead className="text-center font-semibold border-r">TB/CLĐ</TableHead>
                          <TableHead className="text-center font-semibold">TB/ngày</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* Theo m3 gỗ */}
                        <TableRow className="hover:bg-green-50">
                          <TableCell className="font-medium bg-green-50 whitespace-nowrap border-r">
                            🌲 Theo m3 gỗ
                          </TableCell>
                          <TableCell className="text-center font-semibold border-r">
                            {formatNumber(packagingStats.avgVolumeWood * packagingStats.totalDays, 2)}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-blue-600 border-r">
                            {formatNumber(packagingStats.avgProductivityWood, 3)}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-green-600">
                            {formatNumber(packagingStats.avgVolumeWood, 2)}
                          </TableCell>
                        </TableRow>
                        
                        {/* Theo m3 thùng */}
                        <TableRow className="hover:bg-blue-50">
                          <TableCell className="font-medium bg-blue-50 whitespace-nowrap border-r">
                            📦 Theo m3 thùng
                          </TableCell>
                          <TableCell className="text-center font-semibold border-r">
                            {formatNumber(packagingStats.avgVolumeCarton * packagingStats.totalDays, 2)}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-blue-600 border-r">
                            {formatNumber(packagingStats.avgProductivityCarton, 3)}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-green-600">
                            {formatNumber(packagingStats.avgVolumeCarton, 2)}
                          </TableCell>
                        </TableRow>
                        
                        {/* Theo container */}
                        <TableRow className="hover:bg-amber-50">
                          <TableCell className="font-medium bg-amber-50 whitespace-nowrap border-r">
                            🚛 Theo container
                          </TableCell>
                          <TableCell className="text-center font-semibold border-r">
                            {formatNumber(packagingStats.totalContainers, 0)}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-blue-600 border-r">
                            {formatNumber(packagingStats.totalContainers / packagingStats.totalLabor, 3)}
                          </TableCell>
                          <TableCell className="text-center font-semibold text-green-600">
                            {formatNumber(packagingStats.avgContainerCount, 1)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Không có dữ liệu để hiển thị bảng tổng hợp</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Packaging Charts - Show only M3 thùng related values */}
          <PackagingCharts filters={filters} />

          {/* Packaging Data Table - Show all values */}
          <PackagingTable filters={filters} />
        </TabsContent>
        </Tabs>
        </div>
      )}
    </div>
  );
}